import React, { useCallback, useEffect, useState, useRef } from 'react';
interface ProductData {
  title: string;
  price: string;
  thumbnailUrl: string;
}
interface ToastData {
  name: string;
  city: string;
  product: ProductData;
}
const NAMES = ['Maria', 'Sarah', 'Elena', 'Jessica', 'Carmen', 'Emily', 'Ana', 'Ashley', 'Sofia', 'Amanda', 'Isabella', 'Melissa', 'Camila', 'Nicole', 'Valentina', 'Stephanie', 'Valeria', 'Rebecca', 'Lucia', 'Laura', 'Daniela', 'Rachel', 'Victoria', 'Megan', 'Martina', 'Lauren', 'Juliana', 'Hannah', 'Gabriela', 'Samantha', 'Natalia', 'Olivia', 'Emma', 'Mia', 'Charlotte', 'Amelia'];
const CITIES = ['New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX', 'Phoenix, AZ', 'Philadelphia, PA', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'San Jose, CA', 'Austin, TX', 'Jacksonville, FL', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC', 'San Francisco, CA', 'Indianapolis, IN', 'Seattle, WA', 'Denver, CO', 'Washington, DC', 'Boston, MA', 'El Paso, TX', 'Nashville, TN', 'Detroit, MI', 'Las Vegas, NV'];
const FALLBACK_PRODUCT: ProductData = {
  title: 'The Woman 90 Protocol',
  price: '$6.39',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/9iRWJgXntauaBbij2iE5Nq/The_woman_90_-_checkout_image.png'
};
export function RecentPurchaseToast() {
  const [isVisible, setIsVisible] = useState(false);
  const [toastData, setToastData] = useState<ToastData | null>(null);
  const [scannedProduct, setScannedProduct] = useState<ProductData>(FALLBACK_PRODUCT);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const recentNamesRef = useRef<string[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);
  // DOM Scanner
  useEffect(() => {
    const scanDOM = () => {
      try {
        let title = FALLBACK_PRODUCT.title;
        let price = FALLBACK_PRODUCT.price;
        let thumbnailUrl = FALLBACK_PRODUCT.thumbnailUrl;
        // Try to find price
        const priceRegex = /\$\s?\d+(\.\d{2})?/;
        const textNodes = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
        let node;
        while (node = textNodes.nextNode()) {
          if (node.nodeValue && priceRegex.test(node.nodeValue)) {
            const match = node.nodeValue.match(priceRegex);
            if (match && match[0] !== '$197' && match[0] !== '$435' && match[0] !== '$97') {
              // Avoid strikethrough prices
              price = match[0];
              break;
            }
          }
        }
        // Try to find title (look for h2/h3 near pricing)
        const headings = Array.from(document.querySelectorAll('h2, h3'));
        const protocolHeading = headings.find(h => h.textContent?.includes('Protocol'));
        if (protocolHeading && protocolHeading.textContent) {
          title = protocolHeading.textContent.trim();
        }
        // Try to find image
        const images = Array.from(document.querySelectorAll('img'));
        const productImg = images.find(img => img.src.includes('checkout_image') || img.src.includes('promo_image'));
        if (productImg) {
          thumbnailUrl = productImg.src;
        }
        setScannedProduct({
          title,
          price,
          thumbnailUrl
        });
      } catch (e) {
        // Fallback is already set in state
        console.warn('DOM scanning failed, using fallback product data.');
      }
    };
    // Run scanner after a short delay to ensure DOM is ready
    setTimeout(scanDOM, 1000);
  }, []);
  const getRandomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
  const getNextName = useCallback(() => {
    let availableNames = NAMES.filter(name => !recentNamesRef.current.includes(name));
    // If we've used almost all names, reset the history
    if (availableNames.length === 0) {
      recentNamesRef.current = [];
      availableNames = NAMES;
    }
    const nextName = getRandomItem(availableNames);
    // Update history
    recentNamesRef.current.push(nextName);
    if (recentNamesRef.current.length > 10) {
      recentNamesRef.current.shift();
    }
    return nextName;
  }, []);
  const showNextToast = useCallback(() => {
    const name = getNextName();
    const city = getRandomItem(CITIES);
    setToastData({
      name,
      city,
      product: scannedProduct
    });
    setIsVisible(true);
    // Hide after 4 seconds
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      // Schedule next toast (random interval between 8-15s)
      const nextInterval = Math.floor(Math.random() * (15000 - 8000 + 1)) + 8000;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(showNextToast, nextInterval);
    }, 4000);
  }, [getNextName, scannedProduct]);
  // Initial start
  useEffect(() => {
    // First toast after 3 seconds
    timeoutRef.current = setTimeout(showNextToast, 3000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [showNextToast]);
  if (!toastData) return null;
  const animationClass = prefersReducedMotion ? isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none' : isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none';
  return <div className="fixed bottom-6 left-0 right-0 z-40 flex justify-center px-4 pointer-events-none" aria-live="polite" role="status" data-id="element-149">
      <div className={`bg-white rounded-xl shadow-warm-lg border border-gold/20 p-3 max-w-sm w-full flex items-center gap-4 transition-all duration-500 ease-out ${animationClass}`} data-id="element-150">
        <img src={toastData.product.thumbnailUrl} alt={toastData.product.title} className="w-12 h-12 rounded-lg object-cover border border-cream-dark flex-shrink-0" data-id="element-151" />
        <div className="flex-1 min-w-0" data-id="element-152">
          <p className="text-sm text-brown font-body leading-tight" data-id="element-153">
            <span className="font-bold" data-id="element-154">{toastData.name}</span> in{' '}
            {toastData.city} just purchased{' '}
            <span className="font-medium italic" data-id="element-155">
              {toastData.product.title}
            </span>
          </p>
          <p className="text-xs font-bold text-sage mt-1" data-id="element-156">
            {toastData.product.price}
          </p>
        </div>
      </div>
    </div>;
}